// frontend/src/pages/ProfessionLanding.js
//
// ONE generic landing page for every profession. It is the live-mesh homepage
// shell with a profession's words in it: same `.hl-root` token scope, same
// background stack, same section components. Adding a profession page is
// therefore writing `src/content/professions/<slug>.js` and adding a route -
// there is deliberately nothing here to copy-paste per profession.
//
// Everything visible comes from the `content` module (validated by
// `assertProfessionContent`). If you find yourself adding a string to THIS
// file, it belongs in the content module instead.
//
// IT MOUNTS THE HOMEPAGE'S FULL SECTION SET (owner decision 2026-09-22).
// FeaturesLive ("כל מה שצריך, במקום אחד") and TypesLive ("סוגי הזדמנויות") are
// mounted VERBATIM - they take only `isHebrew`, carry no content props, and the
// homepage's copy is exactly what the owner asked a profession page to say.
// Order: features lands in its own homepage slot, right after how-it-works, and
// the profession's concrete `opportunity` cards lead the generic
// `opportunity-types` trio that widens them (specific before generic). An
// earlier round deliberately left both out; that decision is reversed, and the
// free wins that came with it are the three outbound internal links the
// features tiles carry and the `#supporter-network` anchor tile 04 owns.
// BlogLive IS mounted, pinned to the profession's own three articles - it is
// the deliberate bridge to the blog.
//
// Motion/prerender contract, inherited verbatim from Homepage.js: everything
// readable is in the DOM at mount, `html.lf-motion-off` and
// `window.__PRERENDER__` force the final static state, and the lazy mesh sits
// behind `useHydrated` so its Suspense boundary is never part of a snapshot.

import React, { Suspense } from "react";
import { Box } from "@mui/material";
import AuthLayout from "../components/AuthLayout";
import Seo from "../components/Seo";
import HeroLive from "../components/homepage/HeroLive";
import TickerLive from "../components/homepage/TickerLive";
import ClientsLive from "../components/homepage/ClientsLive";
import ProseSection from "../components/homepage/ProseSection";
import FeaturesLive from "../components/homepage/FeaturesLive";
import OpportunitySection from "../components/homepage/OpportunitySection";
import TypesLive from "../components/homepage/TypesLive";
import BlogLive from "../components/homepage/BlogLive";
import HowItWorksLive, { SMOKE_SLOT_ID } from "../components/homepage/HowItWorksLive";
import PricingSection from "../components/homepage/PricingSection";
import NumbersLive from "../components/homepage/NumbersLive";
import CtaLive from "../components/homepage/CtaLive";
import FaqLive from "../components/homepage/FaqLive";
import SceneWashes from "../components/homepage/SceneWashes";
import { buildFaqJsonLdFromItems } from "../components/homepage/faqData";
import professionRegistry from "../content/professions/registry";
import { buildCanonicalUrl, SITE_URL } from "../utils/canonicalUrl";
import useLiveMeshPageChrome from "../homepage/useLiveMeshPageChrome";
import useHydrated from "../motion/useHydrated";
import "../homepage/homepage.css";

// Same split as the homepage: the mesh engine is the biggest thing on the page
// and nothing readable depends on it, so it must not sit in the chunk that has
// to arrive before the hero can paint. Mounted inside a null Suspense.
const MeshCanvas = React.lazy(() => import("../components/homepage/MeshCanvas"));

/**
 * The anchor ids a profession page carries, in render order.
 *
 * DEFINED IN THE REGISTRY, re-exported here: `scripts/prerender.js` asserts
 * each one is present and carries text in the snapshot (the per-page twin of
 * `HOMEPAGE_SECTION_IDS`), and that script is plain node, so it cannot read
 * this module. The list used to be a hand-kept copy in each file with a test
 * holding them level; now there is one list and nothing to drift.
 */
export const { PROFESSION_SECTION_IDS } = professionRegistry;

/**
 * Service + FAQPage + BreadcrumbList, following the sibling landing pages'
 * shapes (`FacebookGroupScanningLanding.js`).
 *
 * Deliberately NO Organization/WebSite node and no SoftwareApplication: those
 * are the homepage's job, and a second copy of the entity on another URL is
 * noise at best.
 *
 * @param {object} content a validated profession content module
 */
export function buildProfessionJsonLd(content) {
  // Built, never hand-concatenated: canonicalUrl.js documents
  // `${SITE_URL}${path}/` as the exact pattern that drifts from the <link
  // rel=canonical> the page emits, and structured data that names a different
  // URL than the canonical tag is a mismatch Google resolves against us. Same
  // output today; drift-proof tomorrow.
  const url = buildCanonicalUrl(content.route);
  const service = {
    "@context": "https://schema.org",
    "@type": "Service",
    // A stable node identity for the page's own service, so a future node can
    // reference it instead of repeating it.
    "@id": `${url}#service`,
    name: content.jsonld.serviceName,
    serviceType: content.jsonld.serviceType,
    description: content.jsonld.serviceDescription,
    provider: {
      "@type": "Organization",
      name: "Leadify AI",
      url: SITE_URL,
      logo: `${SITE_URL}/android-chrome-512x512.png`,
    },
    areaServed: { "@type": "Country", name: "Israel" },
    url,
    inLanguage: "he",
  };
  // The homepage's own FAQPage builder, fed this profession's array: display
  // markup is stripped by the one helper that knows the rule (faqData.js), not
  // by a second copy of it living here.
  const faqPage = buildFaqJsonLdFromItems(content.faq.map((item) => ({ q: item.q.he, a: item.a.he })));
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "דף הבית", item: buildCanonicalUrl("/") },
      { "@type": "ListItem", position: 2, name: content.jsonld.breadcrumbName, item: url },
    ],
  };
  return [service, faqPage, breadcrumb];
}

/**
 * The page body without the app chrome - what the SEO-invariant test renders
 * (AuthLayout drags in Navbar, Footer and the auth + guide contexts).
 *
 * Hebrew-only on purpose, so AuthLayout's injected `isHebrew` is deliberately
 * ignored here: every string in a profession content module is owner-approved
 * Hebrew, and there is no English rendering of it to switch to.
 *
 * @param {object} props
 * @param {object} props.content a profession content module
 */
export const ProfessionLandingContent = ({ content }) => {
  // Body background, the dev-only capture hooks and the fragment scroll - the
  // same three the homepage runs, from the one module both now import
  // (backlog #278). They were duplicated here verbatim until this round.
  useLiveMeshPageChrome();
  // One render AFTER hydration - a Suspense boundary has no marker in a
  // client-rendered snapshot and React 19 answers a markerless boundary by
  // throwing the whole root away (homepageHydration.test.js locks the homepage
  // twin of this).
  const hydrated = useHydrated();

  return (
    // `hl-root` scopes every design token in homepage.css. NO `overflow` here,
    // ever: it would make this Box a scroll container and the pinned
    // how-it-works section would stick to it instead of the viewport.
    //
    // `dir`/`lang` are stated HERE, not inherited: AuthLayout flips its wrapper
    // to `ltr` for a visitor whose `localStorage.language` is "english", and
    // this page's copy is Hebrew whoever is reading it.
    <Box
      className="hl-root"
      dir="rtl"
      lang="he"
      sx={{ width: "100%", background: "var(--hl-page)" }}
    >
      <Seo
        title={content.seo.title}
        description={content.seo.description}
        path={content.seo.canonical}
        lang="he"
        jsonLd={buildProfessionJsonLd(content)}
      />

      {/* The fixed background stack, painted back to front - DOM order is what
          puts the mesh above the washes (both sit at z-index 0). */}
      <SceneWashes />
      {hydrated && (
        <Suspense fallback={null}>
          <MeshCanvas />
        </Suspense>
      )}
      {/* The smoke plume's portal target, in the same slot as on the homepage:
          after the mesh, before the grain, inside the sections' stacking
          context. */}
      <div id={SMOKE_SLOT_ID} className="hl-smoke-slot" aria-hidden="true" />
      <Box className="hl-grain" aria-hidden="true" />
      <Box className="hl-vignette" aria-hidden="true" />

      {/* `body={null}` drops the hero's three-plate paragraph: a profession hero
          carries the reassurance line in that slot instead. */}
      <HeroLive
        isHebrew
        line1={content.hero.line1}
        line2={content.hero.line2}
        sub={content.hero.sub}
        body={null}
        reassurance={content.hero.reassurance}
        primaryCta={content.hero.primaryCta}
        secondaryCta={content.hero.secondaryCta}
        ctaSize="large"
        micro={content.hero.micro}
        stats={content.hero.stats}
        leads={content.hero.leads}
        fields={content.hero.fields}
      />
      {/* The strip scrolls THIS profession's deck, not the homepage's. */}
      <TickerLive leads={content.hero.leads} />
      <ClientsLive isHebrew note={content.clientsNote} />
      <ProseSection
        id="where"
        eyebrow={content.where.eyebrow}
        title={content.where.h2}
        body={content.where.body}
      />
      {/* The story is the homepage's, but the posts it tells it with are this
          profession's - the feed, the scored card and the push all read this
          deck. */}
      <HowItWorksLive isHebrew leads={content.hero.leads} />
      {/* The homepage's benefits grid - its copy verbatim, but the THREE
          tiles whose artefact names a profession (push alert, chips, drafted
          reply) are re-dressed from `content.features` (owner, 2026-09-22:
          the homepage's labour-lawyer alert and cross-trade chips read wrong
          on a profession page). The chip cycle deals the profession's own
          chips plus its mesh deck. Its tiles carry the page's only outbound
          internal links
          (/facebook-group-scanning, /business-website, /supporter-network)
          plus the `#supporter-network` anchor. EAGER, never lazy: a second
          Suspense boundary on this page would be markerless in the snapshot
          and React 19 would throw the whole root away at hydration. */}
      <FeaturesLive
        isHebrew
        profession={{
          pushAlert: content.features.pushAlert,
          chips: content.features.chips,
          chipPool: Array.from(new Set([...content.features.chips, ...content.hero.fields])),
          draft: content.features.draft,
        }}
      />
      <OpportunitySection
        id="opportunity"
        isHebrew
        eyebrow={content.opportunity.eyebrow}
        title={content.opportunity.h2}
        body={content.opportunity.body}
        cards={content.opportunity.cards}
      />
      {/* The generic three, after this profession's concrete cards: specific
          first, then the widening. Homepage copy verbatim (it takes only
          `isHebrew`), and eager for the same reason FeaturesLive is. */}
      <TypesLive isHebrew />
      <PricingSection isHebrew />
      <NumbersLive isHebrew />
      {/* The second ask, before the FAQ - the owner's "second CTA", and
          deliberately the CLOSING PANEL again rather than the compact band:
          same panel, same heading size, same centred pill, this copy. Two
          panels of one shape on a page is his explicit choice. Eyebrow and sub
          are null, not omitted - omitted would hand it the homepage's own. */}
      <CtaLive
        isHebrew
        trackSection="cta-mid"
        eyebrow={null}
        sub={null}
        title={content.ctaBand.title}
        ctaLabel={content.ctaBand.ctaLabel}
      />
      <FaqLive isHebrew items={content.faq} />
      <CtaLive
        isHebrew
        trackSection="cta-end"
        eyebrow={content.cta.eyebrow}
        title={content.cta.title}
        sub={content.cta.sub}
        ctaLabel={content.cta.ctaLabel}
      />
      {/* The blog bridge: the homepage's own three-card row, pinned to this
          profession's articles. */}
      <BlogLive isHebrew slugs={content.blogSlugs} />
    </Box>
  );
};

/**
 * The routed page. `pages/professions/ProfessionPage.js` passes the content module:
 * `<ProfessionLanding content={lawyers} />`.
 *
 * @param {object} props
 * @param {object} props.content
 */
const ProfessionLanding = ({ content }) => (
  <AuthLayout isPublic={true} navVariant="home">
    <ProfessionLandingContent content={content} />
  </AuthLayout>
);

export default ProfessionLanding;
